// Onboarding progress for a manager's direct reports (TF-1500 follow-up).
//
// This module is the single seam between the onboarding UI and its data source.
import { UserStatus } from "types/graphql-schema";

// The three onboarding steps a manager completes for each direct report, in the
// order they are rendered in the progress bar and breakdown.
export const ONBOARDING_METRIC_KEYS = [
  "toldAboutReport",
  "hasRecurringOneonone",
  "invited",
] as const;

export type OnboardingMetricKey = (typeof ONBOARDING_METRIC_KEYS)[number];

export type ReportOnboarding = Record<OnboardingMetricKey, boolean>;

export const ONBOARDING_METRIC_COUNT = ONBOARDING_METRIC_KEYS.length;

export const getCompletedCount = (onboarding: ReportOnboarding): number =>
  ONBOARDING_METRIC_KEYS.filter((key) => onboarding[key]).length;

export const getOnboardingPercent = (onboarding: ReportOnboarding): number =>
  Math.round((getCompletedCount(onboarding) / ONBOARDING_METRIC_COUNT) * 100);

export const isFullyOnboarded = (onboarding: ReportOnboarding): boolean =>
  getCompletedCount(onboarding) === ONBOARDING_METRIC_COUNT;

// Parse the manager's onboarding_data JSON and return the report ids they've
// already been walked through (onboarded_report_ids).
export const getOnboardedReportIds = (
  onboardingData?: string | null,
): number[] => {
  if (!onboardingData) return [];
  try {
    const data = JSON.parse(onboardingData);
    const ids = data?.onboarded_report_ids;
    return Array.isArray(ids) ? ids.map(Number) : [];
  } catch {
    return [];
  }
};

// A report counts as "on the app" once they've joined (active/demo); a report
// still sitting on an invite has status `invited`.
export const isReportJoined = (status?: UserStatus | null): boolean =>
  status === UserStatus.Active || status === UserStatus.Demo;

// Build a report's onboarding state from real data:
// - toldAboutReport: the report id is in the manager's onboarded_report_ids
// - hasRecurringOneonone: there is an upcoming 1-on-1 with the report
// - invited: the report has joined the app (status active/demo)
export const deriveReportOnboarding = ({
  toldAboutReport,
  hasUpcomingOneonone,
  invited,
}: {
  toldAboutReport: boolean;
  hasUpcomingOneonone: boolean;
  invited: boolean;
}): ReportOnboarding => ({
  toldAboutReport,
  hasRecurringOneonone: hasUpcomingOneonone,
  invited,
});
